/*
 * Journal arama — hp-journal-search section'ı.
 *
 * Kutu boşken odaklanınca "Öne çıkan yazı" bloklarının paneli açılır; yazmaya başlanınca kapanır.
 * Kutuya yazıldıkça Shopify predictive search'ten SADECE yazıları (article) ister ve
 * sonucu hp-journal-search-results section'ı olarak render ettirir; dönen HTML'deki
 * [data-hp-journal-results] sonuç paneline konur. Enter / "Tüm sonuçları gör" → /search sayfası.
 * Aynı anda en fazla bir panel açık: #show(panel) diğerini kapatır, #show(null) hepsini.
 */
(() => {
  if (window.customElements.get('hp-journal-search')) return;

  const MIN_LENGTH = 2;
  const DEBOUNCE_MS = 250;

  class HpJournalSearch extends HTMLElement {
    #input;
    #panel;
    #featured;
    #status;
    #timer;
    #listeners;
    #request;
    #cache = new Map();

    connectedCallback() {
      this.#input = this.querySelector('input[name="q"]');
      this.#panel = this.querySelector('[data-hp-journal-panel]');
      this.#featured = this.querySelector('[data-hp-journal-featured]');
      this.#status = this.querySelector('[data-hp-journal-status]');
      if (!this.#input || !this.#panel) return;

      this.#listeners = new AbortController();
      const { signal } = this.#listeners;

      this.#input.addEventListener('input', () => this.#onInput(), { signal });
      this.#input.addEventListener('focus', () => this.#reopen(), { signal });
      // Escape ile kapatılan panel, odak hâlâ kutudayken tekrar tıklanınca açılsın
      this.#input.addEventListener('click', () => this.#reopen(), { signal });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
        // Açık panel varken Escape kutudaki metni silmesin (type="search" varsayılanı), sadece kapatsın.
        // Önce odak: input'a dönüş focus handler'ında paneli yeniden açıyor, ardından kapat
        event.preventDefault();
        this.#input.focus();
        this.#show(null);
      }, { signal });

      // Tab ile bileşenden çıkınca kapan. relatedTarget boşsa (panelde odaklanamayan
      // bir yere tıklama) karışma; dışarı tıklamayı pointerdown yakalıyor.
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.#show(null);
      }, { signal });

      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.#show(null);
      }, { signal });

      // Customizer'da "Öne çıkan yazı" bloğu seçilince panel açık görünsün
      if (window.Shopify?.designMode && this.#featured) {
        this.addEventListener('shopify:block:select', () => this.#show(this.#featured), { signal });
        this.addEventListener('shopify:block:deselect', () => this.#show(null), { signal });
      }
    }

    disconnectedCallback() {
      this.#listeners?.abort();
      this.#request?.abort();
      clearTimeout(this.#timer);
    }

    #reopen() {
      const term = this.#input.value.trim();

      if (term === '') {
        this.#show(this.#featured);
      } else if (term.length >= MIN_LENGTH && this.#panel.hasChildNodes()) {
        this.#show(this.#panel);
      }
    }

    #onInput() {
      clearTimeout(this.#timer);
      const term = this.#input.value.trim();

      if (term.length < MIN_LENGTH) {
        this.#request?.abort();
        this.#panel.replaceChildren();
        // Kutu tamamen boşalınca öne çıkanlar geri gelir; tek harfte panel kapalı kalır
        this.#show(term === '' ? this.#featured : null);
        return;
      }

      // Yazmaya başlanınca öne çıkanlar kapanır; önceki sonuçlar açıksa yenisi gelene kadar kalır
      if (this.#featured && !this.#featured.hidden) this.#show(null);
      this.#timer = setTimeout(() => this.#search(term), DEBOUNCE_MS);
    }

    async #search(term) {
      this.#request?.abort();
      const request = new AbortController();
      this.#request = request;
      this.setAttribute('aria-busy', 'true');

      try {
        const results = this.#cache.get(term) ?? (await this.#fetchResults(term, request.signal));
        this.#cache.set(term, results);

        // Cevap gelene kadar metin değiştiyse eski sonucu basma
        if (term !== this.#input.value.trim()) return;

        this.#panel.replaceChildren(results.cloneNode(true));
        if (this.#status) {
          this.#status.textContent = results.querySelector('[data-hp-journal-status-text]')?.textContent.trim() ?? '';
        }

        // Cevap gelene kadar kullanıcı bileşenden çıktıysa paneli kendiliğinden açma
        if (this.contains(document.activeElement)) this.#show(this.#panel);
      } catch (error) {
        if (error.name !== 'AbortError') this.#show(null);
      } finally {
        if (this.#request === request) this.removeAttribute('aria-busy');
      }
    }

    async #fetchResults(term, signal) {
      const url = new URL(`${window.Shopify?.routes?.root ?? '/'}search/suggest`, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'article');
      url.searchParams.set('resources[limit]', this.getAttribute('limit') || '6');
      url.searchParams.set('resources[options][fields]', 'title,tag,body');
      url.searchParams.set('section_id', this.getAttribute('results-section'));

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Journal search failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = doc.querySelector('[data-hp-journal-results]');
      if (!results) throw new Error('Journal search results not found');

      return document.importNode(results, true);
    }

    #show(panel) {
      this.#panel.hidden = panel !== this.#panel;
      if (this.#featured) this.#featured.hidden = panel !== this.#featured;
      this.classList.toggle('is-open', Boolean(panel));
    }
  }

  window.customElements.define('hp-journal-search', HpJournalSearch);
})();
